import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from 'src/app/_services/api.service';
import { GlobalService } from 'src/app/_services/global.service';
import { ToolbarService, SortService } from '@syncfusion/ej2-angular-grids';
import { interval, Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';
import {
  isPlaylistBloccata,
  PLAYLIST_FERMA_MINUTI_DEFAULT,
  playlistBloccataMessaggio,
} from './monitor-playlist-alert.util';

@Component({
  selector: 'app-monitors',
  templateUrl: './monitors.component.html',
  styleUrls: ['./monitors.component.css'],
  providers: [SortService, ToolbarService],
})
export class MonitorsComponent implements OnInit, OnDestroy {
  @ViewChild('monitorstable') monitorstable: any;
  monitorsList: any[] = [];
  defaultImage = './assets/img/offline.png';
  showArchived = false;
  autoRefresh = true;
  readonly autoRefreshSeconds = 30;
  updateSubscription: Subscription;
  readonly offlineMinuti = 3;
  playlistFermaMinuti = PLAYLIST_FERMA_MINUTI_DEFAULT;
  readonly isPlaylistBloccata = isPlaylistBloccata;
  readonly playlistBloccataMessaggio = playlistBloccataMessaggio;

  readonly statoLegenda: Array<{ key: string; label: string; color: string }> = [
    { key: 'play', label: 'In riproduzione', color: 'Green' },
    { key: 'standby', label: 'Standby', color: 'Black' },
    { key: 'offline', label: 'Offline', color: 'Red' },
    { key: 'error', label: 'Errore', color: 'Orange' },
  ];

  public toolbar: string[];

  constructor(
    private apiService: ApiService,
    public globalService: GlobalService,
    public router: Router
  ) {}

  ngOnInit(): void {
    this.toolbar = ['Search'];
    this.loadPlaylistAlertSetup();
    this.startAutoRefresh();
  }

  get monitorsConPlaylistBloccata(): any[] {
    return this.monitorsList.filter((m) =>
      isPlaylistBloccata(m, this.playlistFermaMinuti, this.offlineMinuti)
    );
  }

  private loadPlaylistAlertSetup(): void {
    this.apiService.post('kodi/monitor_setup_get', {}).subscribe(
      (data: any) => {
        const row = data?.Result?.[0] || data?.Result;
        if (row?.minuti_playlist_ferma != null) {
          this.playlistFermaMinuti = Number(row.minuti_playlist_ferma) || PLAYLIST_FERMA_MINUTI_DEFAULT;
        }
      },
      () => {
        /* usa default */
      }
    );
  }

  /** Pallino stato: rosso = offline, verde = Play, nero = Sleep, arancio = errore */
  statoMonitor(record: any): 'play' | 'standby' | 'offline' | 'error' {
    if (record?.stato_colore) {
      const colore = String(record.stato_colore).toLowerCase();
      if (colore === 'green') {
        return 'play';
      }
      if (colore === 'black') {
        return 'standby';
      }
      if (colore === 'red') {
        return 'offline';
      }
      if (colore === 'orange' || colore === 'yellow') {
        return 'error';
      }
    }

    if (!this.isOnline(record)) {
      return 'offline';
    }
    if (record.azione_corrente === 'Sleep') {
      return 'standby';
    }
    if (record.azione_corrente === 'Play') {
      return 'play';
    }

    const azione = String(record?.azione_corrente || '').toLowerCase();
    if (azione.includes('error') || azione.includes('errore')) {
      return 'error';
    }

    return 'error';
  }

  statoLabel(record: any): string {
    const labels: Record<'play' | 'standby' | 'offline' | 'error', string> = {
      play: 'In riproduzione',
      standby: 'Standby',
      offline: 'Offline',
      error: 'Errore',
    };
    return labels[this.statoMonitor(record)];
  }

  qualeAzione(record: any): string {
    const colori: Record<'play' | 'standby' | 'offline' | 'error', string> = {
      play: 'Green',
      standby: 'Black',
      offline: 'Red',
      error: 'Orange',
    };
    return colori[this.statoMonitor(record)];
  }

  isOnline(record: any): boolean {
    if (record.online === true || record.online === false) {
      return record.online;
    }
    const last = record.last_daemon_at || record.last_update;
    if (!last) {
      return false;
    }
    const diff = Date.now() - new Date(last).getTime();
    return diff < this.offlineMinuti * 60000;
  }

  statoTooltip(record: any): string {
    const label = this.statoLabel(record);
    if (!this.isOnline(record)) {
      return label + ' (nessun segnale demone da oltre ' + this.offlineMinuti + ' min)';
    }
    const last = record.last_daemon_at || record.last_update;
    const dettaglio = record.azione_corrente ? ' — ' + record.azione_corrente : '';
    return label + dettaglio + ' — ultimo segnale: ' + this.formatDateTime(last);
  }

  screenshotUrl(record: any): string {
    return record?.screenshotDisplayUrl || this.defaultImage;
  }

  private applyScreenshotUrl(record: any): void {
    if (!record?.last_screenshot_url) {
      record.screenshotDisplayUrl = this.defaultImage;
      return;
    }
    const cacheBust = record.last_screenshot_at || record.id || '';
    record.screenshotDisplayUrl = record.last_screenshot_url + '?t=' + encodeURIComponent(String(cacheBust));
  }

  formatDateTime(value: any): string {
    if (!value) {
      return '—';
    }
    const d = new Date(value);
    if (isNaN(d.getTime())) {
      return '—';
    }
    return d.toLocaleString('it-IT');
  }

  startAutoRefresh(): void {
    if (this.updateSubscription) {
      this.updateSubscription.unsubscribe();
    }

    if (!this.autoRefresh) {
      this.fetchMonitors();
      return;
    }

    this.updateSubscription = interval(this.autoRefreshSeconds * 1000)
      .pipe(startWith(0))
      .subscribe(() => this.fetchMonitors());
  }

  onAutoRefreshChange(): void {
    this.startAutoRefresh();
  }

  fetchMonitors(): void {
    const params: any = { archived: this.showArchived };
    this.apiService.post('kodi/monitorslistall', params).subscribe((data2: any) => {
      this.monitorsList = data2.Result || [];
      this.monitorsList.forEach((m: any) => {
        if (m.struttura_id == 99999) {
          m.struttura = 'Sede Centrale';
        }
        this.applyScreenshotUrl(m);
      });
      if (this.monitorstable) {
        this.monitorstable.localeObj.currentLocale.EmptyRecord =
          'Nessun monitor da visualizzare ...';
        this.monitorstable.refresh();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.updateSubscription) {
      this.updateSubscription.unsubscribe();
    }
  }

  changeArchived(): void {
    this.startAutoRefresh();
  }

  onLoad(): void {
    this.monitorstable.localeObj.currentLocale.EmptyRecord = 'Caricamento in corso...';
  }

  apri(data: any): void {
    this.router.navigate(['/dashboard/monitors/monitor-edit/update/' + data.id]);
  }

  onImgError(event: any): void {
    event.target.src = this.defaultImage;
  }
}
